import {sanitizeInput} from 'utils/helper';

export const COMPLY_CUBE_CLIENTS_KEY = 'getComplyCubeClients';
export const COMPLY_CUBE_CHECKS_KEY = 'getComplyCubeChecks';
export const COMPLY_CUBE_DOCUMENTS_KEY = 'getComplyCubeDocuments';
export const COMPLY_CUBE_ADDRESSES_KEY = 'getComplyCubeAddresses';
export const COMPLY_CUBE_LIVE_PHOTOS_KEY = 'getComplyCubeLivePhotos';

export function unwrapListResponse(data, queryKey, successTypename) {
  const node = data?.[queryKey];
  if (node?.__typename === 'Error') {
    return {error: node, rows: [], total: null};
  }
  if (node?.__typename === successTypename) {
    const total = node.pagination?.total != null ? node.pagination.total : null;
    return {error: null, rows: node.data || [], total};
  }
  return {error: null, rows: [], total: null};
}

/** ComplyCube list inputs use PaginationMetaInput: { pagination: { page, limit }, ...filters } */
function withPagination(page, limit, fields = {}) {
  return {
    pagination: {page, limit},
    ...fields,
  };
}

export function buildClientsInput(page, limit, {search, nationality}) {
  return withPagination(page, limit, {
    ...(search?.trim() && {search: sanitizeInput(search.trim())}),
    ...(nationality?.trim() && {nationality: nationality.trim()}),
  });
}

export function buildChecksInput(
  page,
  limit,
  {search, clientId, type, status, outcome},
) {
  return withPagination(page, limit, {
    ...(search?.trim() && {search: sanitizeInput(search.trim())}),
    ...(clientId?.trim() && {clientId: clientId.trim()}),
    ...(type?.trim() && {type: type.trim()}),
    ...(status?.trim() && {status: status.trim()}),
    ...(outcome?.trim() && {outcome: outcome.trim()}),
  });
}

export function buildDocumentsInput(
  page,
  limit,
  {search, clientId, type, issuingCountry},
) {
  return withPagination(page, limit, {
    ...(search?.trim() && {search: sanitizeInput(search.trim())}),
    ...(clientId?.trim() && {clientId: clientId.trim()}),
    ...(type?.trim() && {type: type.trim()}),
    ...(issuingCountry?.trim() && {issuingCountry: issuingCountry.trim()}),
  });
}

export function buildAddressesInput(page, limit, {search, clientId, country}) {
  return withPagination(page, limit, {
    ...(search?.trim() && {search: sanitizeInput(search.trim())}),
    ...(clientId?.trim() && {clientId: clientId.trim()}),
    ...(country?.trim() && {country: country.trim()}),
  });
}

export function buildLivePhotosInput(page, limit, {search, clientId}) {
  return withPagination(page, limit, {
    ...(search?.trim() && {search: sanitizeInput(search.trim())}),
    ...(clientId?.trim() && {clientId: clientId.trim()}),
  });
}

const GREEN = [
  'CLEAR',
  'COMPLETE',
  'COMPLETED',
  'PASSED',
  'SUCCESS',
  'APPROVED',
];
const RED = [
  'ATTENTION',
  'FAILED',
  'FAIL',
  'REJECTED',
  'DECLINED',
  'ERROR',
  'CANCELLED',
];
const YELLOW = ['PENDING', 'PROCESSING', 'IN_PROGRESS', 'AWAITING', 'REVIEW'];

function normalizeToken(s) {
  return (s || '').toString().trim().toUpperCase().replace(/\s+/g, '_');
}

export function badgeToneForStatus(value) {
  const v = normalizeToken(value);
  if (!v) return 'default';
  if (GREEN.some(g => v.includes(g) || g.includes(v))) return 'success';
  if (RED.some(r => v.includes(r) || r.includes(v))) return 'error';
  if (YELLOW.some(y => v.includes(y) || y.includes(v))) return 'warning';
  return 'default';
}

export function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

/** e.g. identity_check → Identity Check */
export function formatCheckTypeLabel(value) {
  if (!value) return '-';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function formatBytes(n) {
  if (n == null || n === '') return '-';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

export function isLikelyHttpUrl(s) {
  if (!s || typeof s !== 'string') return false;
  return /^https?:\/\//i.test(s.trim());
}

export function firstHttpUrl(...candidates) {
  for (const c of candidates) {
    if (c != null && isLikelyHttpUrl(String(c))) return String(c).trim();
  }
  return null;
}

/** Parse a private S3 URL into the bucket + filename needed to presign it. */
export function parseS3Url(url) {
  if (!url || typeof url !== 'string' || !url.includes('amazonaws.com')) {
    return null;
  }
  try {
    const parsed = new URL(url);
    const bucket = parsed.hostname.split('.')[0];
    const filename = decodeURIComponent(parsed.pathname).replace(/^\//, '');
    if (!bucket || !filename) return null;
    return {bucket, filename};
  } catch {
    return null;
  }
}
